import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import CircularProgress from '@mui/material/CircularProgress';
import CheckIcon from '@mui/icons-material/Check';
import Eyebrow from '../components/common/Eyebrow';
import LocationPicker from '../components/common/LocationPicker';
import PrepChecklist from '../components/booking/PrepChecklist';
import { useCart } from '../context/CartContext';
import { useUI } from '../context/UIContext';
import { useAuth } from '../context/AuthContext';
import { useCreateBookingMutation, useInitiatePayuMutation } from '../store/bookingsApi';
import { ROUTES } from '../constants/routes';

const STEPS = ['Schedule', 'Details', 'Checklist', 'Payment', 'Confirm'];

const SLOTS = ['6:00 AM', '7:00 AM', '8:00 AM', '9:00 AM', '10:00 AM', '11:00 AM', '2:00 PM', '4:00 PM', '6:00 PM', '8:00 PM'];

const getNextDays = (n) => {
  const days = [];
  const now = new Date();
  for (let i = 0; i < n; i++) {
    const d = new Date(now);
    d.setDate(now.getDate() + i);
    days.push({
      key: d.toISOString().slice(0, 10),
      day: d.toLocaleDateString('en-IN', { weekday: 'short' }),
      date: d.getDate(),
      month: d.toLocaleDateString('en-IN', { month: 'short' }),
      isToday: i === 0,
    });
  }
  return days;
};

const Booking = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { items, totals } = useCart();
  const { showToast } = useUI();
  const { user, isAuthenticated } = useAuth();
  const [createBooking, { isLoading: confirming }] = useCreateBookingMutation();
  const [initiatePayu, { isLoading: initiatingPay }] = useInitiatePayuMutation();

  const [step, setStep] = useState(0);
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState(null);
  const [patient, setPatient] = useState({
    name: '', age: '', gender: 'Male', phone: '',
    addr: '',
    // ── full location block ──
    lat: null, lng: null, accuracy: null, place_id: null,
    street_number: '', street: '', sublocality: '', area: '',
    city: '', district: '', state: '', country: '', country_code: '', pincode: '',
  });
  const [pay, setPay] = useState('payu');
  const [prepAcknowledged, setPrepAcknowledged] = useState(false);
  const [bookingId, setBookingId] = useState(null);

  // Auth gate — must be logged in to book
  useEffect(() => {
    if (!isAuthenticated) {
      navigate(`${ROUTES.LOGIN}?next=${encodeURIComponent(ROUTES.BOOKING)}`, { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Prefill patient from logged-in user
  useEffect(() => {
    if (user && !patient.name) {
      setPatient((p) => ({
        ...p,
        name: user.name || '',
        phone: user.phone || '',
        age: user.age || '',
        gender: user.gender ? user.gender.charAt(0).toUpperCase() + user.gender.slice(1) : 'Male',
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // If PayU redirected us back with a failure, surface it as a toast and clear the query.
  // Kept at the top (above any early returns) so the hook order stays stable.
  useEffect(() => {
    const payStatus = searchParams.get('payment');
    if (!payStatus) return;
    if (payStatus === 'failed') {
      showToast(`Payment failed for ${searchParams.get('booking') || 'your booking'} — please try again`);
    } else if (payStatus === 'invalid') {
      showToast('Payment response could not be verified — contact support');
    } else if (payStatus === 'not_found') {
      showToast('Booking not found for that payment');
    }
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const days = useMemo(() => getNextDays(7), []);
  const grand = totals.subtotal;

  if (items.length === 0 && !bookingId) {
    return (
      <Container sx={{ py: 14, textAlign: 'center' }}>
        <Typography className="serif" variant="h3" sx={{ mb: 2 }}>
          Your cart is empty
        </Typography>
        <Button variant="contained" onClick={() => navigate(ROUTES.TESTS)}>
          Browse Tests
        </Button>
      </Container>
    );
  }

  const canContinue = () => {
    if (step === 0) return !!date && !!slot;
    if (step === 1) return patient.name && patient.phone && patient.addr;
    if (step === 2) return prepAcknowledged;
    if (step === 3) return !!pay;
    return true;
  };

  const next = () => {
    if (!canContinue()) {
      showToast('Please fill all required fields');
      return;
    }
    if (step < STEPS.length - 1) setStep(step + 1);
  };

  // Builds a hidden form and submits it so the browser navigates to PayU.
  const redirectToPayU = ({ action, method, params }) => {
    const form = document.createElement('form');
    form.method = method || 'POST';
    form.action = action;
    Object.entries(params || {}).forEach(([k, v]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = k;
      input.value = v == null ? '' : String(v);
      form.appendChild(input);
    });
    document.body.appendChild(form);
    form.submit();
  };

  const confirm = async () => {
    try {
      const payload = {
        items: items.map((i) => ({
          item_id: String(i.id),
          item_type: i.type,
          name: i.name,
          price: Number(i.price) || 0,
          original: i.original ?? null,
          icon: i.icon || '🧪',
          tat: i.tat || '',
          paramCount: i.paramCount || 0,
          quantity: i.quantity || 1,
        })),
        schedule: { date, slot },
        patient: {
          name: patient.name,
          age: patient.age ? Number(patient.age) : undefined,
          gender: (patient.gender || 'other').toLowerCase(),
          phone: patient.phone,
          address: patient.addr,
          location: patient.lat != null && patient.lng != null
            ? {
                lat: patient.lat,
                lng: patient.lng,
                accuracy: patient.accuracy,
                place_id: patient.place_id,
                street_number: patient.street_number,
                street: patient.street,
                sublocality: patient.sublocality,
                area: patient.area,
                city: patient.city,
                district: patient.district,
                state: patient.state,
                country: patient.country,
                country_code: patient.country_code,
                pincode: patient.pincode,
              }
            : undefined,
        },
        payment_method: pay,
        discount: 0,
      };
      const res = await createBooking(payload).unwrap();
      const booking = res?.booking;
      const newId = booking?.booking_id;
      setBookingId(newId);

      // Booking was created in `awaiting_payment` state — invisible to
      // admin/listings until the PayU callback flips it to 'confirmed'.
      // Initiate PayU and redirect; cart is cleared server-side after
      // PayU verifies the transaction.
      const init = await initiatePayu({ booking_id: newId }).unwrap();
      showToast('Redirecting to PayU — do not close this tab');
      redirectToPayU(init);
    } catch (err) {
      showToast(err?.data?.message || 'Could not place the booking. Please try again.');
    }
  };

  return (
    <Box sx={{ bgcolor: 'var(--cream)', minHeight: '90vh' }}>
      <Box sx={{ bgcolor: '#fff', borderBottom: '1px solid var(--border-l)', pt: { xs: 10, md: 12 }, pb: 3 }}>
        <Container maxWidth="xl" sx={{ maxWidth: 1200, px: { xs: 2, md: 4 } }}>
          <Eyebrow>Checkout</Eyebrow>
          <Typography className="serif" variant="h2" sx={{ mt: 1, mb: 3 }}>
            Book your Trustian
          </Typography>
          <Stepper
            activeStep={step}
            alternativeLabel
            sx={{
              '& .MuiStepLabel-label': {
                fontSize: { xs: 11, md: 13 },
                mt: { xs: 0.5, md: 1 },
              },
              '& .MuiStepConnector-root': {
                top: { xs: 10, md: 12 },
              },
            }}
          >
            {STEPS.map((s) => (
              <Step key={s}>
                <StepLabel>{s}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ maxWidth: 1200, py: { xs: 3, md: 4 }, px: { xs: 2, md: 4 } }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
            gap: { xs: 2, md: 3 },
            width: '100%',
            alignItems: 'start',
          }}
        >
          <Box>
            <Box sx={{ bgcolor: '#fff', border: '1px solid var(--border-l)', borderRadius: '20px', p: { xs: 3, md: 4 } }}>
              {/* Step 0: Schedule */}
              {step === 0 && (
                <>
                  <Typography className="serif" variant="h3" sx={{ mb: 1 }}>
                    Pick date & time
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: 'var(--stone)', mb: 3, fontWeight: 300 }}>
                    Your Trustian arrives within a 30-min window of your chosen slot.
                  </Typography>

                  <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--stone)', mb: 1.5 }}>
                    Date
                  </Typography>
                  <Stack direction="row" spacing={1.25} sx={{ mb: 2, overflowX: 'auto', pb: 1 }}>
                    {days.map((d) => {
                      const active = date === d.key;
                      return (
                        <Box
                          key={d.key}
                          onClick={() => setDate(d.key)}
                          sx={{
                            minWidth: 72,
                            textAlign: 'center',
                            p: 1.5,
                            borderRadius: '14px',
                            cursor: 'pointer',
                            border: '1.5px solid',
                            borderColor: active ? 'var(--sage)' : 'var(--border-l)',
                            bgcolor: active ? 'var(--sage)' : '#fff',
                            transition: 'all 0.2s',
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 10,
                              fontWeight: 700,
                              letterSpacing: '0.1em',
                              textTransform: 'uppercase',
                              color: active ? 'rgba(255,255,255,0.85)' : 'var(--stone)',
                            }}
                          >
                            {d.isToday ? 'Today' : d.day}
                          </Typography>
                          <Typography
                            className="serif"
                            sx={{
                              fontSize: 22,
                              fontWeight: 700,
                              my: 0.25,
                              color: active ? '#fff' : 'var(--ink)',
                            }}
                          >
                            {d.date}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 10,
                              color: active ? 'rgba(255,255,255,0.75)' : 'var(--stone)',
                            }}
                          >
                            {d.month}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Stack>

                  {/* Custom date picker */}
                  <Box sx={{ mb: 4 }}>
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: 'var(--stone)',
                        mb: 0.75,
                      }}
                    >
                      Or pick a specific date
                    </Typography>
                    <Box
                      component="input"
                      type="date"
                      value={date || ''}
                      min={new Date().toISOString().slice(0, 10)}
                      onChange={(e) => setDate(e.target.value)}
                      sx={{
                        width: '100%',
                        maxWidth: 280,
                        border: '1.5px solid var(--border-l)',
                        borderRadius: '12px',
                        px: 1.75,
                        py: 1.25,
                        fontSize: 14,
                        fontWeight: 600,
                        color: 'var(--ink)',
                        bgcolor: '#fff',
                        fontFamily: '"Outfit", sans-serif',
                        outline: 'none',
                        cursor: 'pointer',
                        '&:focus': { borderColor: 'var(--sage)' },
                      }}
                    />
                  </Box>

                  <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--stone)', mb: 1.5 }}>
                    Time Slot
                  </Typography>
                  <Grid container spacing={1.25}>
                    {SLOTS.map((s) => {
                      const active = slot === s;
                      return (
                        <Grid item xs={6} sm={4} md={3} key={s}>
                          <Box
                            onClick={() => setSlot(s)}
                            sx={{
                              textAlign: 'center',
                              py: 1.25,
                              borderRadius: '10px',
                              border: '1.5px solid',
                              borderColor: active ? 'var(--sage)' : 'var(--border-l)',
                              bgcolor: active ? 'var(--sage)' : '#fff',
                              color: active ? '#fff' : 'var(--ink)',
                              fontSize: 13,
                              fontWeight: 700,
                              cursor: 'pointer',
                            }}
                          >
                            {s}
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>

                  {/* Custom time picker */}
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: 'var(--stone)',
                        mb: 0.75,
                      }}
                    >
                      Or pick a specific time
                    </Typography>
                    <Box
                      component="input"
                      type="time"
                      value={
                        slot && /^\d{2}:\d{2}$/.test(slot)
                          ? slot
                          : ''
                      }
                      onChange={(e) => {
                        const v = e.target.value;
                        if (!v) {
                          setSlot(null);
                          return;
                        }
                        const [hStr, mStr] = v.split(':');
                        const h = parseInt(hStr, 10);
                        const period = h >= 12 ? 'PM' : 'AM';
                        const hr12 = ((h + 11) % 12) + 1;
                        setSlot(`${hr12}:${mStr} ${period}`);
                      }}
                      sx={{
                        width: '100%',
                        maxWidth: 280,
                        border: '1.5px solid var(--border-l)',
                        borderRadius: '12px',
                        px: 1.75,
                        py: 1.25,
                        fontSize: 14,
                        fontWeight: 600,
                        color: 'var(--ink)',
                        bgcolor: '#fff',
                        fontFamily: '"Outfit", sans-serif',
                        outline: 'none',
                        cursor: 'pointer',
                        '&:focus': { borderColor: 'var(--sage)' },
                      }}
                    />
                  </Box>
                </>
              )}

              {/* Step 1: Details */}
              {step === 1 && (
                <>
                  <Typography className="serif" variant="h3" sx={{ mb: 1 }}>
                    Patient & address details
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: 'var(--stone)', mb: 3, fontWeight: 300 }}>
                    The Trustian will visit this address at your selected slot.
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={8}>
                      <TextField
                        label="Patient Full Name *"
                        fullWidth
                        value={patient.name}
                        onChange={(e) => setPatient({ ...patient, name: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Age *"
                        fullWidth
                        type="number"
                        inputProps={{ min: 0, max: 130 }}
                        value={patient.age}
                        onChange={(e) => setPatient({ ...patient, age: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Phone *"
                        fullWidth
                        value={patient.phone}
                        onChange={(e) => setPatient({ ...patient, phone: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        select
                        label="Gender *"
                        fullWidth
                        value={patient.gender || 'Male'}
                        onChange={(e) => setPatient({ ...patient, gender: e.target.value })}
                      >
                        <MenuItem value="Male">Male</MenuItem>
                        <MenuItem value="Female">Female</MenuItem>
                        <MenuItem value="Other">Other</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12}>
                      <LocationPicker
                        value={{
                          address: patient.addr,
                          lat: patient.lat,
                          lng: patient.lng,
                          place_id: patient.place_id,
                          accuracy: patient.accuracy,
                          street_number: patient.street_number,
                          street: patient.street,
                          sublocality: patient.sublocality,
                          area: patient.area,
                          city: patient.city,
                          district: patient.district,
                          state: patient.state,
                          country: patient.country,
                          country_code: patient.country_code,
                          pincode: patient.pincode,
                        }}
                        onChange={(loc) => setPatient((p) => ({
                          ...p,
                          addr: loc.address || '',
                          lat: loc.lat ?? null,
                          lng: loc.lng ?? null,
                          place_id: loc.place_id ?? null,
                          accuracy: loc.accuracy ?? null,
                          street_number: loc.street_number || '',
                          street: loc.street || '',
                          sublocality: loc.sublocality || '',
                          area: loc.area || '',
                          city: loc.city || '',
                          district: loc.district || '',
                          state: loc.state || '',
                          country: loc.country || '',
                          country_code: loc.country_code || '',
                          pincode: loc.pincode || '',
                        }))}
                      />
                    </Grid>
                  </Grid>
                </>
              )}

              {/* Step 2: Checklist — blood-test preparation */}
              {step === 2 && (
                <PrepChecklist
                  acknowledged={prepAcknowledged}
                  onAcknowledgeChange={setPrepAcknowledged}
                />
              )}

              {/* Step 3: Payment */}
              {step === 3 && (
                <>
                  <Typography className="serif" variant="h3" sx={{ mb: 1 }}>
                    Payment method
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: 'var(--stone)', mb: 3, fontWeight: 300 }}>
                    Pay securely online — your booking is confirmed only after payment succeeds.
                  </Typography>
                  <RadioGroup value={pay} onChange={(e) => setPay(e.target.value)}>
                    <Stack spacing={1.5}>
                      {[
                        ['payu', '💳', 'Pay Online (PayU)', 'Cards · UPI · Net banking · Wallets — secure 3DS'],
                      ].map(([v, ico, t, d]) => {
                        const active = pay === v;
                        return (
                          <Box
                            key={v}
                            onClick={() => setPay(v)}
                            sx={{
                              p: 2,
                              border: '1.5px solid',
                              borderColor: active ? 'var(--sage)' : 'var(--border-l)',
                              bgcolor: active ? 'var(--mint)' : '#fff',
                              borderRadius: '14px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                            }}
                          >
                            <Typography sx={{ fontSize: 24 }}>{ico}</Typography>
                            <Box sx={{ flex: 1 }}>
                              <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{t}</Typography>
                              <Typography sx={{ fontSize: 12, color: 'var(--stone)', fontWeight: 300 }}>{d}</Typography>
                            </Box>
                            <Radio checked={active} value={v} />
                          </Box>
                        );
                      })}
                    </Stack>
                  </RadioGroup>
                </>
              )}

              {/* Step 4: Confirm */}
              {step === 4 && (
                <>
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Box
                      sx={{
                        width: 72,
                        height: 72,
                        borderRadius: '50%',
                        bgcolor: 'var(--sage)',
                        color: '#fff',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2,
                      }}
                    >
                      <CheckIcon sx={{ fontSize: 36 }} />
                    </Box>
                    <Typography className="serif" variant="h3" sx={{ mb: 1 }}>
                      Review & confirm
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: 'var(--stone)', fontWeight: 300 }}>
                      Your booking ID will be <strong style={{ color: 'var(--forest)' }}>{bookingId}</strong>
                    </Typography>
                  </Box>
                  <Stack spacing={1.5} sx={{ mb: 2 }}>
                    <SummaryRow k="Date & Slot" v={`${date || '-'} · ${slot || '-'}`} />
                    <SummaryRow k="Patient" v={`${patient.name} · ${patient.age}y · ${patient.gender}`} />
                    <SummaryRow k="Phone" v={patient.phone} />
                    <SummaryRow k="Address" v={patient.addr} />
                    <SummaryRow k="Payment" v={pay.toUpperCase()} />
                  </Stack>
                </>
              )}

              {/* Footer buttons */}
              <Stack direction="row" justifyContent="space-between" sx={{ mt: 4, pt: 3, borderTop: '1px solid var(--border-l)' }}>
                <Button
                  disabled={step === 0}
                  onClick={() => setStep(step - 1)}
                  sx={{ color: 'var(--stone)', fontWeight: 700 }}
                >
                  ← Back
                </Button>
                {step < STEPS.length - 1 ? (
                  <Button variant="contained" color="primary" size="large" onClick={next}>
                    Continue →
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={confirm}
                    disabled={confirming || initiatingPay}
                    startIcon={(confirming || initiatingPay) ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null}
                  >
                    {confirming ? 'Placing booking…' : initiatingPay ? 'Redirecting to PayU…' : 'Pay & Confirm'}
                  </Button>
                )}
              </Stack>
            </Box>
          </Box>

          {/* Summary — column stretches to the row height (overrides the
              container's alignItems:start) so the card inside has room to
              travel and stick to the top while the left side scrolls. */}
          <Box sx={{ alignSelf: 'stretch' }}>
            <Box
              sx={{
                bgcolor: '#fff',
                border: '1.5px solid var(--border-l)',
                borderRadius: '20px',
                p: 3,
                position: { md: 'sticky' },
                top: 90,
              }}
            >
              <Typography className="serif" variant="h3" sx={{ mb: 2.5 }}>
                Your order
              </Typography>
              <Stack spacing={1.75} sx={{ pb: 2, mb: 2, borderBottom: '1px dashed var(--border-l)' }}>
                {items.map((it) => (
                  <Stack key={`${it.type}-${it.id}`} direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                    <Typography sx={{ fontSize: 13, color: 'var(--charcoal)', flex: 1 }}>
                      {it.icon} {it.name}
                    </Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)' }}>
                      ₹{it.price.toLocaleString()}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
              <Stack spacing={1} sx={{ pb: 2, mb: 2, borderBottom: '1px dashed var(--border-l)' }}>
                <Row k="Subtotal" v={`₹${totals.subtotal.toLocaleString()}`} />
                <Row k="Home collection" v="Free" highlight />
              </Stack>
              <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Total</Typography>
                <Typography className="serif" sx={{ fontSize: 26, fontWeight: 700, color: 'var(--forest)' }}>
                  ₹{grand.toLocaleString()}
                </Typography>
              </Stack>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

const Row = ({ k, v, highlight }) => (
  <Stack direction="row" justifyContent="space-between">
    <Typography sx={{ fontSize: 12.5, color: 'var(--charcoal)' }}>{k}</Typography>
    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: highlight ? 'var(--sage)' : 'var(--ink)' }}>{v}</Typography>
  </Stack>
);

const SummaryRow = ({ k, v }) => (
  <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ py: 1, borderBottom: '1px dashed var(--border-l)' }}>
    <Typography sx={{ fontSize: 12, color: 'var(--stone)', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
      {k}
    </Typography>
    <Typography sx={{ fontSize: 13, color: 'var(--ink)', textAlign: 'right', maxWidth: '60%' }}>{v}</Typography>
  </Stack>
);

export default Booking;
